// We define what our expected API error structure looks like.
interface ApiError {
  response: {
    status: number
    data: {
      error: string
    }
  }
}

/**
 * Type Guard: Checks if the error matches our API error structure.
 */
export const isApiError = (error: unknown): error is ApiError => {
  // 1. Check if 'error' is a non-null object.
  if (typeof error !== 'object' || error === null) {
    return false
  }

  // 2. Check if 'error' has a 'response' property.
  // We can safely use the 'in' operator now.
  if (!('response' in error)) {
    return false
  }

  // 3. Check if 'response' is a non-null object.
  // We create an intermediate variable to avoid casting 'error' repeatedly.
  const response = (error as { response: unknown }).response
  if (typeof response !== 'object' || response === null) {
    return false
  }

  // 4. Check if 'response' has 'status' and 'data' properties.
  if (!('status' in response) || !('data' in response)) {
    return false
  }

  // 5. Check if 'data' is a non-null object.
  const data = (response as { data: unknown }).data
  if (typeof data !== 'object' || data === null) {
    return false
  }

  // 6. Finally, check if 'data' has a 'error' property which is a string.
  if (!('error' in data) || typeof (data as { error: unknown }).error !== 'string') {
    return false
  }

  // If all checks passed, it's a valid ApiError.
  return true
}

/**
 * Extracts an error message from various error types (Error, string, object).
 * Serves as a fallback when the error is not a specific API error.
 */
export const getGenericErrorMessage = (error: unknown): string => {
  const defaultMessage = 'An unexpected error occurred. Please try again.'

  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === 'string' && error.length > 0) {
    return error
  }

  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message
  }

  return defaultMessage
}
